import { Input, Table, Segmented, Button, Flex } from 'antd'
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Device } from '@/type/stationType'
import useOneStationStore from '@/stores/oneStation'
import { getDeviceStatus } from '@/utils/getStatus'
import formatDate from '@/utils/formatDate'

const DeviceFilter: React.FC<{
  setShowDeviceList: (value: (Device & { status: string })[]) => void
  device: (Device & { status: string })[]
}> = ({ setShowDeviceList, device }) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState('all')
  const [segmentedOption, setSegmentedOption] = useState<{ label: JSX.Element | string; value: string }[]>([])
  const [nameSN, setNameSN] = useState('')

  useEffect(() => {
    setSegmentedOption([
      { label: t('status.all'), value: 'all' },
      {
        label: <div className='Devicelists-Online Devicelists-before'>{t('status.online')}</div>,
        value: 'online',
      },
      {
        label: <div className='Devicelists-Offline Devicelists-before'>{t('status.offline')}</div>,
        value: 'offline',
      },
      {
        label: <div className='Devicelists-Warning Devicelists-before'>{t('status.warning')}</div>,
        value: 'warning',
      },
      {
        label: <div className='Devicelists-Fault Devicelists-before'>{t('status.fault')}</div>,
        value: 'fault',
      },
    ])
  }, [t])

  const handleFilter = (value?: string) => {
    if (value) {
      setStatus(value)
    } else {
      value = status
    }
    let deviceTemp = device.filter(
      item => item.obj_name.toLowerCase().includes(nameSN.toLowerCase()) || item.device_code.includes(nameSN)
    )
    if (value !== 'all') {
      deviceTemp = deviceTemp.filter(item => item.status === value)
    }
    setShowDeviceList(deviceTemp)
  }

  return (
    <div className='Devicelists-header'>
      <Segmented value={status} onChange={handleFilter} options={segmentedOption} />
      <div className='Devicelists-header-search'>
        <Input placeholder={t('device.nameSN')} value={nameSN} onChange={e => setNameSN(e.target.value)} />
        <Button type='primary' ghost onClick={() => handleFilter()}>
          {t('common.search')}
        </Button>
      </div>
    </div>
  )
}

const DeviceTable: React.FC<{ showDevice: (Device & { status: string })[] }> = ({ showDevice }) => {
  const { t } = useTranslation()
  const device_type = useOneStationStore(state => state.device_type)
  const device = useOneStationStore(state => state.device)

  type DeviceTableItem = {
    id: number
    device: { id: number; name: string; sn: string; image: string | undefined; type: string }
    status: string
    type: string
    gatewaySN: string
    lastUpdate: string
    action: string[]
  }
  const [tableDevice, setTableDevice] = useState<DeviceTableItem[]>([])
  const navigate = useNavigate()

  useEffect(() => {
    // console.log(showDevice)
    const deviceTemp: DeviceTableItem[] = showDevice.map(item => ({
      id: item.id,
      device: {
        id: item.id,
        name: item.obj_name,
        sn: item.device_code,
        image: item.device_pic,
        type: item.device_type,
      },
      status: item.status.charAt(0).toUpperCase() + item.status.slice(1),
      type: device_type.find(type => type.code_id === item.device_type)?.code_name as string,
      gatewaySN:
        item.parent_id == 0 ? '' : (device.find(dev => dev.device_id === item.parent_id)?.device_code as string),
      lastUpdate: formatDate(new Date(item.update_time)),
      action: ['edit', 'delete'],
    }))
    setTableDevice(deviceTemp)
  }, [showDevice])

  const [columns, setColumns] = useState<any[]>([])

  useEffect(() => {
    setColumns([
      {
        title: t('device.deviceName'),
        dataIndex: 'device',
        fixed: 'left',
        width: '30%',
        render: (v: any) => {
          const { type, name, sn, image, id } = v
          return (
            <div
              className='table-name'
              onClick={() => {
                if (type === 'C002') {
                  navigate(`../inverter/${id}/devicedetail`)
                }
              }}>
              <img src={image} />
              <div className='table-name-text'>
                <span>{name}</span>
                <span>SN:{sn}</span>
              </div>
            </div>
          )
        },
      },
      {
        title: t('device.deviceStatus'),
        dataIndex: 'status',
        width: '10%',
        render: (v: any) => {
          return <div className={`Devicelists-${v} Devicelists-before table-status`}>{v}</div>
        },
      },
      {
        title: t('device.deviceType'),
        dataIndex: 'type',
        width: '10%',
      },
      // {
      //   title: "MAIN HMI Firmware",
      //   dataIndex: "MHF",
      //   width: 200,
      // },
      // {
      //   title: "LCD Firmware",
      //   dataIndex: "LF",
      //   width: 200,
      // },
      // {
      //   title: "Real Time",
      //   dataIndex: "RealTime",
      //   width: 200,
      // },
      {
        title: t('device.gatewaySN'),
        dataIndex: 'gatewaySN',
        width: '20%',
      },
      {
        title: t('device.lastUpdateTime'),
        dataIndex: 'lastUpdate',
        width: '20%',
      },
      {
        title: t('common.action'),
        dataIndex: 'action',
        width: '10%',
        render: (v: any, r: any) => {
          return (
            <Flex gap={15}>
              {v.map((v1: any) => (
                <img className='actionImg' src={`/assets/web/${v1}.svg`} key={v1} />
              ))}
            </Flex>
          )
        },
      },
    ])
  }, [t])

  return (
    <Table
      scroll={{
        x: 'max-content',
        y: window.innerHeight - 365,
      }}
      dataSource={tableDevice}
      columns={columns}
      rowKey={'id'}
    />
  )
}

const DeviceList: React.FC = () => {
  const device = useOneStationStore(state => state.device)
  const mqtt_serve = useOneStationStore(state => state.mqtt_serve)
  const [deviceWithStatus, setDeviceWithStatus] = useState<(Device & { status: string })[]>([])
  const [showDeviceList, setShowDeviceList] = useState<(Device & { status: string })[]>([])

  useEffect(() => {
    deviceEffectFun()
  }, [device])

  const deviceEffectFun = async () => {
    const deviceTemp = device.map(item => ({ ...item, status: '' }))
    for (let oneDevice of deviceTemp) {
      oneDevice.status = (await getDeviceStatus(
        mqtt_serve[oneDevice.mqtt_server_id],
        oneDevice.iot_gateway_uuid,
        oneDevice.device_id,
        oneDevice.device_type
      )) as string
    }
    setDeviceWithStatus(deviceTemp)
    setShowDeviceList(deviceTemp)
  }

  return (
    <div className='Devicelists' id='Devicelists'>
      <DeviceFilter device={deviceWithStatus} setShowDeviceList={setShowDeviceList} />
      <DeviceTable showDevice={showDeviceList} />
    </div>
  )
}

export default DeviceList
